import { useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import {
  useApplyProductInfoUsageApi,
  useProductInfoUsageApi,
  useSaveProductInfoUsageApi
} from '@/common/api/modules/productInfoUsage'
import { productUnderstandingPath } from '@/common/app/path'
import { PiuMatrix, PIU_FIELD_LABEL, PIU_TARGET_LABEL } from '@/common/model/productInfoUsage'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' }) : '—'

const ProductInfoUsageMatrixSection = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const navigate = useNavigate()

  // 反映中は useProductInfoUsageApi 側が自動でポーリングする
  const { data, mutate, isLoading } = useProductInfoUsageApi({ site_id: siteId })

  // 編集中の値。サーバの値を土台にして、変更点だけ上書きする
  const [draft, setDraft] = useState<PiuMatrix | null>(null)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [applying, setApplying] = useState(false)

  // サーバの値が変わったときだけ編集中の値を作り直す（描画中の state 更新。
  // 効果で書くと、保存やポーリングのたびに編集内容が飛ぶ）。
  // ポーリング中は matrix が変わらないので、ここは通らない
  const serverKey = data ? JSON.stringify(data.matrix) : null
  if (serverKey && serverKey !== loadedKey) {
    setLoadedKey(serverKey)
    setDraft(data ? data.matrix : null)
  }

  const running = data?.running_application ?? null

  const { trigger: save } = useSaveProductInfoUsageApi({
    onSuccess: () => {
      setSaving(false)
      mutate()
    },
    onError: () => setSaving(false)
  })
  const { trigger: apply } = useApplyProductInfoUsageApi({
    onSuccess: () => {
      setApplying(false)
      // 取り直すと running_application が入り、ポーリングが自動で始まる
      mutate()
    },
    onError: () => setApplying(false)
  })

  const dirty = !!serverKey && !!draft && serverKey !== JSON.stringify(draft)

  const pending = data?.pending_targets ?? []
  // 抽出より後に変更された「再抽出が要る項目」。変えていない項目まで促すと
  // 毎回の課金を勧めることになるため、変更があったときだけ出す
  const reExtractNeeded = data?.product_understanding?.changed_since_extract ?? []

  if (isLoading || !data || !draft) {
    return (
      <Area>
        <AreaContent>
          <Text size={13}>読み込み中...</Text>
        </AreaContent>
      </Area>
    )
  }

  const toggle = (usageKey: string, fieldKey: string, value: boolean) => {
    setDraft((prev) =>
      prev ? { ...prev, [usageKey]: { ...prev[usageKey], [fieldKey]: value } } : prev
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      <Area>
        <Heading subText='商品マスタのどの情報を、どの用途で使うかを選びます。初期状態は現在の検索・レコメンドの挙動と同じです（商品概要と詳細説明の語句検索だけ、もともと使っていないため「適用しない」から始まります）。'>
          利用する商品情報
        </Heading>
        <AreaContent>
          <div className='mt-3'>
            <Table>
              <thead>
                <TableRow head>
                  <TableHeadItem>
                    <Text size={12}>商品情報</Text>
                  </TableHeadItem>
                  {data.usages.map((u) => (
                    <TableHeadItem key={u.key}>
                      <div>
                        <Text size={12}>{u.label}</Text>
                      </div>
                      <Text size={10} color={TextColor.TERTIARY}>
                        {u.description}
                      </Text>
                    </TableHeadItem>
                  ))}
                </TableRow>
              </thead>
              <tbody>
                {data.fields.map((f) => (
                  <TableRow key={f.key}>
                    <TableRowItem>
                      <div className='flex items-center gap-2'>
                        <Text size={13}>{f.label}</Text>
                        {f.required && <Badge badgeType={BadgeType.SQUARE_TERTIARY}>必須</Badge>}
                      </div>
                      <Text size={10} color={TextColor.TERTIARY}>
                        {f.source}
                      </Text>
                    </TableRowItem>
                    {data.usages.map((u) => {
                      // その用途で読んでいない項目は設定させない。
                      // 出すと「ONにしたのに効かない」が起きる
                      if (!u.applicable_fields.includes(f.key)) {
                        const reason = u.not_applicable_reasons?.[f.key]
                        return (
                          <TableRowItem key={u.key}>
                            <Text size={12} color={TextColor.TERTIARY}>
                              使用しません
                            </Text>
                            {/* 理由を必ず添える。「使用しません」だけだと、
                                理由が無いのに使えないように見える */}
                            {reason && (
                              <div>
                                <Text size={10} color={TextColor.TERTIARY}>
                                  {reason}
                                </Text>
                              </div>
                            )}
                          </TableRowItem>
                        )
                      }
                      // 8軸に溶け込んでいて分離できない項目は、外しても
                      // 商品意味理解を実行し直すまで完全には効かない
                      const needsReExtract =
                        data.product_understanding.active &&
                        u.re_extraction_fields?.includes(f.key)
                      return (
                        <TableRowItem key={u.key}>
                          <CheckBox
                            name={`${u.key}:${f.key}`}
                            checked={draft[u.key]?.[f.key] ?? true}
                            disabled={f.required}
                            label={f.required ? '常に適用' : '適用'}
                            onChange={(v) => toggle(u.key, f.key, v)}
                          />
                          {needsReExtract && (
                            <div>
                              <Text size={10} color={TextColor.TERTIARY}>
                                変更は商品意味理解の再実行後に完全に反映されます
                              </Text>
                            </div>
                          )}
                        </TableRowItem>
                      )
                    })}
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </div>

          <div className='mt-4 flex items-center gap-3'>
            <Button
              disabled={!dirty || saving}
              loading={saving}
              onClick={() => {
                setSaving(true)
                save({ site_id: siteId, matrix: draft })
              }}
            >
              保存する
            </Button>
            {dirty && (
              <Text size={12} color={TextColor.CAUTION}>
                未保存の変更があります
              </Text>
            )}
            <Text size={12} color={TextColor.SECONDARY}>
              最終保存： {fmtDateTime(data.last_saved_at)}
            </Text>
          </div>
        </AreaContent>
      </Area>

      {reExtractNeeded.length > 0 && (
        <Area>
          <Heading subText='索引を作り直すだけでは、これらの変更は完全には反映されません。'>
            商品意味理解の再実行が必要です
          </Heading>
          <AreaContent>
            <div className='mt-3 flex flex-col gap-2'>
              <div className='flex flex-wrap items-center gap-2'>
                {reExtractNeeded.map((c) => (
                  <Badge key={`${c.usage_key}:${c.field_key}`} badgeType={BadgeType.SQUARE_CAUTION}>
                    {PIU_FIELD_LABEL[c.field_key] ?? c.field_key}
                    {c.enabled ? '（適用）' : '（適用しない）'}
                  </Badge>
                ))}
              </div>
              <div>
                <Text size={12} color={TextColor.SECONDARY}>
                  意味検索・会話が使う商品の要約は、キャッチコピー・商品概要・詳細説明を
                  まとめて読み取って作られています。どれか1つを外しても、要約に残った内容までは
                  消せません。完全に反映するには商品意味理解を実行し直してください
                  （AIの利用料が発生します）。最後の実行： {fmtDateTime(data.product_understanding.last_extracted_at)}
                </Text>
              </div>
              <div>
                <Button
                  color={ButtonColor.SECONDARY}
                  onClick={() => navigate(productUnderstandingPath(siteId))}
                >
                  商品意味理解を開く
                </Button>
              </div>
            </div>
          </AreaContent>
        </Area>
      )}

      <Area>
        <Heading subText='保存しただけでは検索結果は変わりません。索引（辞書・ベクトル・レコメンド）を作り直して初めて反映されます。'>
          設定の反映
        </Heading>
        <AreaContent>
          {running ? (
            <div className='mt-3 flex flex-col gap-2'>
              <div>
                <Text size={13}>
                  反映中：{' '}
                  {running.targets.map((t) => PIU_TARGET_LABEL[t] ?? t).join(' / ')}
                </Text>
              </div>
              <div>
                <Text size={12} color={TextColor.SECONDARY}>
                  開始 {fmtDateTime(running.started_at)}。この画面を閉じても処理は続きます。
                  {/* ベクトルの注意書きは、実際にベクトルを含むときだけ出す。
                      辞書だけの反映（数秒で終わる）にこの文言を出すと、待たせる処理だと誤解させる */}
                  {running.targets.includes('vector') &&
                    'ベクトルは商品数ぶんの処理が走るため時間がかかります。'}
                </Text>
              </div>
              <div className='bg-secondary h-2 w-full overflow-hidden rounded-full'>
                <div className='bg-brand h-2 w-1/3 animate-pulse rounded-full' />
              </div>
            </div>
          ) : pending.length === 0 ? (
            <div className='mt-3'>
              <Text size={13} color={TextColor.SUCCESS}>
                すべて反映済みです。
              </Text>
            </div>
          ) : (
            <div className='mt-3 flex flex-col gap-3'>
              <div className='flex flex-wrap items-center gap-2'>
                <Text size={13}>未反映：</Text>
                {pending.map((t) => (
                  <Badge key={t} badgeType={BadgeType.SQUARE_CAUTION}>
                    {PIU_TARGET_LABEL[t] ?? t}
                  </Badge>
                ))}
              </div>
              <div className='flex flex-wrap gap-2'>
                <Button
                  disabled={applying || dirty}
                  loading={applying}
                  onClick={() => {
                    setApplying(true)
                    apply({ site_id: siteId })
                  }}
                >
                  すべて反映する
                </Button>
                {pending.map((t) => (
                  <Button
                    key={t}
                    color={ButtonColor.SECONDARY}
                    disabled={applying || dirty}
                    onClick={() => {
                      setApplying(true)
                      apply({ site_id: siteId, targets: [t] })
                    }}
                  >
                    {PIU_TARGET_LABEL[t] ?? t}だけ反映
                  </Button>
                ))}
              </div>
              {dirty && (
                <div>
                  <Text size={12} color={TextColor.CAUTION}>
                    先に設定を保存してください。
                  </Text>
                </div>
              )}
            </div>
          )}
        </AreaContent>
      </Area>
    </div>
  )
}

export default ProductInfoUsageMatrixSection
